import type { ServiceMapper, ServiceType } from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type {
  CheckoutSkiAncillaryDuration,
  CheckoutSkiAncillarySelectedValue,
  CheckoutSkiAncillaryService,
  CheckoutSkiAncillaryServiceOption,
} from '@/scopes/checkout/domain/types/services/ski'
import type {
  APIBookingDraft,
  APIBookingDraftRoom,
  APIBookingDraftSkiAncillaryServiceOption,
} from '@/scopes/checkout/infrastructure/api/types'
import type { APIBookingDraftSkiAncillaryService } from '@/scopes/checkout/infrastructure/api/types/services/ski'
import { format } from 'date-fns'
import { isNil, values } from 'ramda'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceOptionMapper?: ServiceOptionMapper
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceType: ServiceType
}

const defaultSportType = 'Ski'
const defaultDuration = '6'
export const NoSportType = 'None'

export const createSkiAncillaryServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutSkiAncillaryService> = (dependencies: Dependencies) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(
      ({ aName }) => aName === serviceName,
    ) as unknown as APIBookingDraftSkiAncillaryService

    if (!service)
      return null

    const { aName, options } = service
    const filteredServiceTypeOptions = options.filter(option => option.serviceType === serviceTypeMapper)
    const participants
      = bookingDraft?.participants.reduce((acc, participant) => {
        const anyOptionAppliesToParticipant = filteredServiceTypeOptions.some(({ appliesToParticipants }) =>
          appliesToParticipants.includes(participant.id),
        )
        if (!anyOptionAppliesToParticipant) {
          return acc
        }
        const selectedOption
          = filteredServiceTypeOptions
            .filter(({ isSelected }) => isSelected)
            .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id)) || undefined

        return {
          ...acc,
          [participant.id.toString()]: {
            ...serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, filteredServiceTypeOptions),
            selectedValue: buildUniqOptionId(selectedOption),
            availableOptions: filteredServiceTypeOptions
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(option => buildUniqOptionId(option)),
          },
        }
      }, {}) ?? {}
    const anyParticipantHasSelectedValue = values(participants).some(({ selectedValue }) => selectedValue)

    if (filteredServiceTypeOptions.length === 0) {
      return null
    }
    return {
      id: aName,
      type: serviceType,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      isNoneOption: buildUniqOptionId(filteredServiceTypeOptions.find(({ isNoneOption }) => isNoneOption)) ?? '',
      options: filteredServiceTypeOptions.reduce(
        (acc2: Record<string, CheckoutSkiAncillaryServiceOption>, option) => ({
          ...acc2,
          [buildUniqOptionId(option) ?? '']: {
            ...serviceOptionMapper.map(option, service, bookingDraft),
            id: buildUniqOptionId(option) ?? '',
            duration: option.duration.toString(),
            startDate: getLocaleDateString(new Date(option.startDate)),
            endDate: getLocaleDateString(new Date(option.endDate)),
            level: option.materialCategory || option.sportLevel,
            sportType: option.sportType,
            isIncluded: option.isIncluded ?? false,
          },
        }),
        {},
      ),
      editingSelectedValues: filteredServiceTypeOptions.reduce(
        (acc3: Record<string, CheckoutSkiAncillarySelectedValue>, option) => {
          if (option.isSelected) {
            option.assignedParticipants.forEach((participantId) => {
              acc3[participantId] = {
                duration: option.duration > 0 ? option.duration.toString() : defaultDuration,
                value: option.code,
                id: buildUniqOptionId(option) ?? '',
                level: option.materialCategory || option.sportLevel,
                sportType: option.isNoneOption ? NoSportType : option.sportType,
                startDate: getLocaleDateString(new Date(option.startDate)),
              }
            })
          }
          else {
            bookingDraft.participants.forEach((participant) => {
              if (acc3[participant.id.toString()])
                return

              const defaultStartDateSkiPass = getDefaultStartDateSkiPassForParticipant(options, participant.id)

              let defaultOption
                = filteredServiceTypeOptions.find(
                  ({ duration, sportType, appliesToParticipants, startDate }) =>
                    duration.toString() === defaultDuration
                    && sportType?.toLowerCase() === defaultSportType?.toLowerCase()
                    && startDate === defaultStartDateSkiPass
                    && appliesToParticipants.includes(participant.id),
                )
                || filteredServiceTypeOptions.find(
                  ({ duration, sportType, appliesToParticipants }) =>
                    duration.toString() === defaultDuration
                    && sportType?.toLowerCase() === defaultSportType?.toLowerCase()
                    && appliesToParticipants.includes(participant.id),
                )

              if (!defaultOption) {
                defaultOption = filteredServiceTypeOptions.find(({ isNoneOption }) => !isNoneOption)
              }

              acc3[participant.id.toString()] = {
                sportType: defaultOption?.sportType ?? defaultSportType,
                duration: defaultOption?.duration.toString() ?? defaultDuration,
                value: defaultOption?.code ?? '',
                id: buildUniqOptionId(defaultOption) ?? '',
                level: (defaultOption?.materialCategory || defaultOption?.sportLevel) ?? '',
                startDate: defaultOption?.startDate
                  ? getLocaleDateString(new Date(defaultOption?.startDate ?? ''))
                  : '',
              }
            })
          }
          return acc3
        },
        {},
      ),
      durations: filteredServiceTypeOptions.reduce((acc4: Record<string, CheckoutSkiAncillaryDuration>, option) => {
        if (option.duration === 0)
          return acc4
        acc4[option.duration.toString()] = {
          availableOptions: filteredServiceTypeOptions
            .filter(({ duration, isNoneOption }) => duration === option.duration && !isNoneOption)
            .map(optionToMap => buildUniqOptionId(optionToMap) ?? ''),
          value: option.duration.toString(),
        }
        return acc4
      }, {}),
      levels: filteredServiceTypeOptions.reduce(
        (acc5: Record<string, { levels: { type: string, appliesToParticipants: string[] }[] }>, option) => ({
          ...acc5,
          [option.sportType]: {
            levels: Array.from(
              new Map(
                filteredServiceTypeOptions
                  .filter(({ sportType, isNoneOption }) => sportType === option.sportType && !isNoneOption)
                  .map(({ materialCategory, sportLevel, appliesToParticipants }) => ({
                    type: materialCategory || sportLevel,
                    appliesToParticipants: appliesToParticipants.map(participantId => participantId.toString()),
                  }))
                  .map(item => [item.type, item]), // Create key-value pairs with `type` as the key
              ).values(),
            ),
          },
        }),
        {},
      ),
    }
  }

  function getDefaultStartDateSkiPassForParticipant(
    options: APIBookingDraftSkiAncillaryServiceOption[],
    participantId: number,
  ) {
    if (serviceTypeMapper === 'pass') {
      return ''
    }

    return (
      options.find(
        option =>
          option.serviceType === 'pass' && option.isSelected && option.assignedParticipants.includes(participantId),
      )?.startDate ?? ''
    )
  }

  function buildUniqOptionId(option: APIBookingDraftSkiAncillaryServiceOption | undefined) {
    if (!option)
      return undefined
    return `${option.code}??${option.duration}??${option.startDate}??${option.endDate}`
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(participantId => participantId.toString()),
    }
  }

  function mapSelection(service: CheckoutSkiAncillaryService) {
    const { participants, options } = service
    return Object.values(participants ?? {}).map(({ id, selectedValue }) => {
      const selectedOption = options[selectedValue ?? '']
      return {
        participantId: id,
        value: selectedOption?.value ?? null,
        startDate: selectedOption?.startDate,
        endDate: selectedOption?.endDate,
      }
    })
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutSkiAncillaryService, step: number): ImpressionViewAncillariesItem[] {
    const inputs: SelectedItemInput[] = []
    const seenItems = new Set<string>()

    values(service.participants ?? {}).forEach((participant) => {
      if (!participant.selectedValue || seenItems.has(participant.selectedValue))
        return

      const option = service.options[participant.selectedValue]
      if (!option)
        return

      const participantPrice = getPriceForParticipant(option, participant.id)

      seenItems.add(participant.selectedValue)
      inputs.push({
        label: option.label || '',
        value: option.value,
        price: participantPrice,
      })
    })

    return mapDefaultSelectedItemToGA4(inputs, {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  function getPriceForParticipant(option: CheckoutSkiAncillaryServiceOption | undefined, participantId: string) {
    if (!option)
      return 0

    const assignment = option.priceAssignments?.find(({ participantIds }) => participantIds.includes(participantId))
    return assignment?.price ?? option.price ?? 0
  }

  function getLocaleDateString(date: Date): string {
    return format(date, 'yyyy-MM-dd')
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
